import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { NodeBuilder } from '../../../../../../../shared/models';
import { ChatHubIntegrationNode, NODE_TYPE } from './chathub-integration';
import { ClickatellExpansionPanelComponent } from '../../../../../../../shared/components/clickatell-expansion-panel/clickatell-expansion-panel.component';
import { MatExpansionPanelTitle } from '@angular/material/expansion';
import { ClickatellFormValidationComponent } from '../../../../../../../shared/components/clickatell-form-validation/clickatell-form-validation.component';
import { ActiveFormDirective } from '../../../../../../../shared/directives/active-form/active-form.directive';
import { MatSelect, MatOption, MatSelectChange } from '@angular/material/select';
import { CMSComponent } from '../../../../../../../shared/components/cms/cms.component';
import { NextSelectorComponent } from '../../../../json-to-from/next-selector/next-selector.component';
import { FlowManagerService } from '../../../../../services/flow-manager.service';
import { FlowBuilderService } from '../../../../../services/flowBuilder/flow-builder.service';
import { ChatHubService } from '../../../../../../../core/services/chathub/chat-hub.service';
import { required } from '../../../../../../../shared/utils';
import { swalMini } from '../../../../../../../shared/utils/utils';
import { Observable, Subject } from 'rxjs';
import { map, tap, takeUntil } from 'rxjs/operators';
import { LoadingIconComponent } from '../../../../../../../shared/components/loading-icon/loading-icon.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-chathub-integration',
    templateUrl: './chathub-integration.component.html',
    styleUrls: ['./chathub-integration.component.scss'],
    imports: [
        ReactiveFormsModule,
        ClickatellExpansionPanelComponent,
        MatExpansionPanelTitle,
        ClickatellFormValidationComponent,
        ActiveFormDirective,
        MatSelect,
        MatOption,
        CMSComponent,
        NextSelectorComponent,
        LoadingIconComponent,
        AsyncPipe,
    ],
})
export class ChatHubIntegrationNodeComponent extends NodeBuilder implements OnInit, OnDestroy {

    private chatHubIntegrationNode: ChatHubIntegrationNode;
    private destroy$ = new Subject<void>();

    loading = true;
    providers$: Observable<{ id: string, name: string }[]>;
    selectedProviderName = '';
    messageEndpoints: { id: string, name: string }[] = [];
    mediaEndpoints: { id: string, name: string }[] = [];
    messageEndpointParams: { id: string, label: string, placeholder: string, parameterType: string, isArray: boolean, required: boolean }[] = [];
    mediaEndpointParams: { id: string, label: string, placeholder: string, parameterType: string, isArray: boolean, required: boolean }[] = [];
    instances: { id: string, displayName: string }[] = [];

    constructor(private flowManagerService: FlowManagerService, private flowBuilder: FlowBuilderService, private chatHubService: ChatHubService) {
        super(NODE_TYPE);
    }

    ngOnInit(): void {
        this.chatHubIntegrationNode = new ChatHubIntegrationNode();

        this.providers$ = this.flowManagerService.getChatHubProviders().pipe(
            map((result: any[]) => result.map(p => ({ id: p.id, name: p.name }))),
            tap((providers) => {
                this.loading = false;
                this.initializing = false;
                const selectedId = this.formGroup?.get('providerId')?.value;
                if (selectedId) {
                    this.selectedProviderName = providers.find(p => p.id === selectedId)?.name ?? '';
                }
            }),
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onProviderSelected(event: MatSelectChange): void {
        const providerId = event.value;
        const option = (event.source.selected as any);
        this.selectedProviderName = option?.viewValue?.trim() ?? '';
        this.messageEndpoints = [];
        this.mediaEndpoints = [];
        this.messageEndpointParams = [];
        this.mediaEndpointParams = [];
        this.instances = [];
        // Reset endpoint selections and remove stale dynamic controls
        this.formGroup.get('messageEndpoint')?.reset();
        this.formGroup.get('messageParameterId')?.reset();
        this.formGroup.get('mediaEndpoint')?.reset();
        this.formGroup.get('mediaParameterId')?.reset();
        Object.keys(this.formGroup.controls)
            .filter(k => k.startsWith('msg_') || k.startsWith('media_'))
            .forEach(k => this.formGroup.removeControl(k));

        this.flowManagerService.getChatHubApis(providerId).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (result) => {
                const endpoints = result.map((a: any) => ({ id: a.id, name: a.name }));
                this.messageEndpoints = endpoints;
                this.mediaEndpoints = endpoints;
            },
            error: () => swalMini({ title: 'Failed to load ChatHub APIs', type: 'error' }),
        });

        this.chatHubService.getProviderInstances(providerId).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (result) => {
                this.instances = result ?? [];
                if (this.instances.length === 1) {
                    this.formGroup.get('instanceId').setValue(this.instances[0].id);
                } else if (this.instances.length > 1) {
                    this.formGroup.get('instanceId').enable();
                }
            },
            error: () => swalMini({ title: 'Failed to load ChatHub instances', type: 'error' }),
        });
    }

    onMessageEndpointSelected(event: MatSelectChange): void {
        const providerId = this.formGroup.get('providerId').value;
        // Remove stale msg_ controls from previous endpoint selection
        Object.keys(this.formGroup.controls)
            .filter(k => k.startsWith('msg_'))
            .forEach(k => this.formGroup.removeControl(k));
        this.messageEndpointParams = [];
        this.formGroup.get('messageParameterId')?.reset();
        this.flowManagerService.getChatHubApiDetails(providerId, event.value).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (result) => {
                this.messageEndpointParams = result.requestParameters ?? [];
                this.messageEndpointParams.forEach(p => {
                    const controlKey = `msg_${p.id}`;
                    if (!this.formGroup.contains(controlKey)) {
                        const validators = p.required ? required(p.label ?? p.id) : [];
                        this.formGroup.addControl(controlKey, new UntypedFormControl({ value: '', disabled: !this.canEdit.canEditFlows }, validators));
                    }
                });
                if (this.messageEndpointParams.length === 0) {
                    this.formGroup.get('messageParameterId')?.clearValidators();
                    this.formGroup.get('messageParameterId')?.updateValueAndValidity();
                    this.formGroup.get('messageParameterId')?.disable();
                } else if (this.messageEndpointParams.length === 1) {
                    this.formGroup.get('messageParameterId')?.setValue(this.messageEndpointParams[0].id);
                    this.formGroup.get('messageParameterId')?.disable();
                } else {
                    this.formGroup.get('messageParameterId')?.enable();
                }
            },
            error: () => swalMini({ title: 'Failed to load endpoint details', type: 'error' }),
        });
    }

    onMediaEndpointSelected(event: MatSelectChange): void {
        const providerId = this.formGroup.get('providerId').value;
        // Remove stale media_ controls from previous endpoint selection
        Object.keys(this.formGroup.controls)
            .filter(k => k.startsWith('media_'))
            .forEach(k => this.formGroup.removeControl(k));
        this.mediaEndpointParams = [];
        this.formGroup.get('mediaParameterId')?.reset();
        this.flowManagerService.getChatHubApiDetails(providerId, event.value).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (result) => {
                this.mediaEndpointParams = result.requestParameters ?? [];
                this.mediaEndpointParams.forEach(p => {
                    const controlKey = `media_${p.id}`;
                    if (!this.formGroup.contains(controlKey)) {
                        const validators = p.required ? required(p.label ?? p.id) : [];
                        this.formGroup.addControl(controlKey, new UntypedFormControl({ value: '', disabled: !this.canEdit.canEditFlows }, validators));
                    }
                });
                if (this.mediaEndpointParams.length === 0) {
                    this.formGroup.get('mediaParameterId')?.clearValidators();
                    this.formGroup.get('mediaParameterId')?.updateValueAndValidity();
                    this.formGroup.get('mediaParameterId')?.disable();
                } else if (this.mediaEndpointParams.length === 1) {
                    this.formGroup.get('mediaParameterId')?.setValue(this.mediaEndpointParams[0].id);
                    this.formGroup.get('mediaParameterId')?.disable();
                } else {
                    this.formGroup.get('mediaParameterId')?.enable();
                }
            },
            error: () => swalMini({ title: 'Failed to load endpoint details', type: 'error' }),
        });
    }

    set buildForm(stepData: any) {
        this.formGroup = this.chatHubIntegrationNode.toForm(stepData, this.canEdit);
        this.setAllNextStepValues = ['next', 'error'];

        const chatHubIntegration = stepData?.menu?.chatHubIntegration ?? stepData?.menu?.chatHub ?? {};
        const messageParams = chatHubIntegration?.messageParameters ?? chatHubIntegration?.messageparameters ?? {};
        const mediaParams = chatHubIntegration?.mediaParameters ?? chatHubIntegration?.mediaparameters ?? {};
        const providerId = this.formGroup.get('providerId').value;
        if (providerId) {
            this.flowManagerService.getChatHubApis(providerId).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (result) => {
                    const endpoints = result.map((a: any) => ({ id: a.id, name: a.name }));
                    this.messageEndpoints = endpoints;
                    this.mediaEndpoints = endpoints;

                    const messageEndpointId = this.formGroup.get('messageEndpoint').value;
                    if (messageEndpointId) {
                        this.flowManagerService.getChatHubApiDetails(providerId, messageEndpointId).pipe(
                            takeUntil(this.destroy$)
                        ).subscribe({
                            next: (res) => {
                                this.messageEndpointParams = res.requestParameters ?? [];
                                if (this.messageEndpointParams.length === 0) {
                                    this.formGroup.get('messageParameterId')?.clearValidators();
                                    this.formGroup.get('messageParameterId')?.updateValueAndValidity();
                                    this.formGroup.get('messageParameterId')?.disable();
                                } else {
                                    this.messageEndpointParams.forEach(p => {
                                        const controlKey = `msg_${p.id}`;
                                        if (!this.formGroup.contains(controlKey)) {
                                            const raw = messageParams[p.id]?.value !== undefined ? messageParams[p.id].value : (chatHubIntegration[p.id] ?? '');
                                            const isNull = raw === null;
                                            if (isNull) { p.placeholder = 'null'; }
                                            const validators = p.required ? required(p.label ?? p.id) : [];
                                            this.formGroup.addControl(controlKey, new UntypedFormControl({ value: isNull ? '' : raw, disabled: !this.canEdit.canEditFlows }, validators));
                                        }
                                    });
                                }
                                this.flowBuilder.setNodeValid(this.nodeFlowDetails.stepId, this.isNodeValid);
                            },
                            error: () => {
                                swalMini({ title: 'Failed to load message endpoint details', type: 'error' });
                                this.flowBuilder.setNodeValid(this.nodeFlowDetails.stepId, this.isNodeValid);
                            },
                        });
                    }

                    const mediaEndpointId = this.formGroup.get('mediaEndpoint').value;
                    if (mediaEndpointId) {
                        this.flowManagerService.getChatHubApiDetails(providerId, mediaEndpointId).pipe(
                            takeUntil(this.destroy$)
                        ).subscribe({
                            next: (res) => {
                                this.mediaEndpointParams = res.requestParameters ?? [];
                                if (this.mediaEndpointParams.length === 0) {
                                    this.formGroup.get('mediaParameterId')?.clearValidators();
                                    this.formGroup.get('mediaParameterId')?.updateValueAndValidity();
                                    this.formGroup.get('mediaParameterId')?.disable();
                                } else {
                                    this.mediaEndpointParams.forEach(p => {
                                        const controlKey = `media_${p.id}`;
                                        if (!this.formGroup.contains(controlKey)) {
                                            const raw = mediaParams[p.id]?.value !== undefined ? mediaParams[p.id].value : (chatHubIntegration[p.id] ?? '');
                                            const isNull = raw === null;
                                            if (isNull) { p.placeholder = 'null'; }
                                            const validators = p.required ? required(p.label ?? p.id) : [];
                                            this.formGroup.addControl(controlKey, new UntypedFormControl({ value: isNull ? '' : raw, disabled: !this.canEdit.canEditFlows }, validators));
                                        }
                                    });
                                }
                                this.flowBuilder.setNodeValid(this.nodeFlowDetails.stepId, this.isNodeValid);
                            },
                            error: () => {
                                swalMini({ title: 'Failed to load media endpoint details', type: 'error' });
                                this.flowBuilder.setNodeValid(this.nodeFlowDetails.stepId, this.isNodeValid);
                            },
                        });
                    }

                    if (!messageEndpointId && !mediaEndpointId) {
                        this.flowBuilder.setNodeValid(this.nodeFlowDetails.stepId, this.isNodeValid);
                    }
                },
                error: () => {
                    swalMini({ title: 'Failed to load ChatHub APIs', type: 'error' });
                    this.flowBuilder.setNodeValid(this.nodeFlowDetails.stepId, this.isNodeValid);
                },
            });

            this.chatHubService.getProviderInstances(providerId).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (result) => {
                    this.instances = result ?? [];
                    if (this.instances.length === 1) {
                        this.formGroup.get('instanceId').setValue(this.instances[0].id);
                    } else if (this.instances.length > 1) {
                        this.formGroup.get('instanceId').enable();
                    }
                },
                error: () => swalMini({ title: 'Failed to load ChatHub instances', type: 'error' }),
            });
        }
    }

    get getStepData(): {} {
        this.chatHubIntegrationNode.setNodeData(this.formGroup.getRawValue(), this.messageEndpointParams, this.mediaEndpointParams, this.selectedProviderName);
        return this.chatHubIntegrationNode.nodeData;
    }

    private initializing = true;

    get isNodeValid(): boolean {
        if (!this.initializing) {
            this.formGroup.markAllAsTouched();
        }
        return this.formGroup.valid && !this.loading;
    }
}
